import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';

const ToastContext = createContext(null);
let idSeq = 0;

export function ToastProvider({ children }){
  const [toasts, setToasts] = useState([]);
  const add = useCallback((message, { type='info', duration=4000 }={})=>{
    const id = ++idSeq;
    setToasts(t=> [...t, { id, message, type, created:Date.now(), duration }]);
    return id;
  },[]);
  const remove = useCallback(id=> setToasts(t=> t.filter(x=> x.id!==id)),[]);
  // auto remove
  useEffect(()=>{
    if(!toasts.length) return;
    const timers = toasts.map(t=> setTimeout(()=> remove(t.id), t.duration));
    return ()=> timers.forEach(clearTimeout);
  },[toasts, remove]);
  const api = {
    push: add,
    success: (m,o)=> add(m,{...o,type:'success'}),
    error: (m,o)=> add(m,{...o,type:'error'}),
    info: (m,o)=> add(m,{...o,type:'info'}),
    remove
  };
  return <ToastContext.Provider value={api}>
    {children}
    <div aria-live='polite' aria-atomic='true' className='fixed z-[1000] top-4 right-4 w-72 space-y-2'>
      {toasts.map(t=> <ToastItem key={t.id} toast={t} onClose={()=>remove(t.id)} />)}
    </div>
  </ToastContext.Provider>;
}

function ToastItem({ toast, onClose }){
  const colors = toast.type==='success' ? 'bg-green-600' : toast.type==='error' ? 'bg-red-600' : 'bg-gray-800';
  return <div role='status' className={`text-white text-sm px-4 py-3 rounded-lg shadow-lg flex items-start gap-3 ${colors}`}>
    <div className='flex-1'>{toast.message}</div>
    <button onClick={onClose} className='text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 rounded'>×</button>
  </div>;
}

export function useToast(){
  const ctx = useContext(ToastContext);
  if(!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
